import { Observable, forkJoin, of, switchMap } from 'rxjs';
import { LayoutService } from '../../layout-management/services/layout.service';
import { ProvisioningService } from '../services/provisioning-service/provisioning-service';
import { FileManagerService } from '../../file-manager/services/file-manager.service';
import { DAYS_OF_WEEK, Layout, LayoutRequest } from '../../layout-management/models/layout.model';
import { FileItem } from '../../file-manager/models/file-manager.model';

/**
 * Shared model + business rules for the "assign content" dialog
 * (`assign-content-dialog`) — it builds a draft shape and applies it through
 * a single chain. Kept as a plain, DI-free module (not a service/component)
 * so the dialog can call into it without sharing Angular state.
 */

export type ContentType = 'IMAGE' | 'SVG';
export type SetupStatus = 'loading' | 'ok' | 'noConfig' | 'noProfiles' | 'error';

export interface BreadcrumbEntry {
  id: string | null;
  name: string;
}

export interface PickedFile {
  file: FileItem;
  duration: number;
}

/** A region placed within a layout, with its own image(s)/SVG. `existingPayload`
 *  is set only when this component was rehydrated from an already-saved
 *  layout and hasn't been re-picked in this session — it lets `applyContentAssignment$`
 *  resend the original resolved content refs verbatim instead of needing a
 *  file-manager id (which a saved layout's component doesn't carry) to
 *  re-fetch a SAS URL. */
export interface DraftComponent {
  id: string;
  left: number;
  top: number;
  right: number;
  bottom: number;
  contentType: ContentType;
  files: PickedFile[];
  existingPayload?: unknown;
}

/** One layout to build for the group. Layouts fall out of which activation
 *  events are turned on (one layout per event, except `BUTTON_EVENT`, which
 *  gets one layout per claimed button, and `DEFAULT`, whose count is the
 *  rotation stepper). `originalId` is set when this layout already exists on
 *  the backend (rehydrated) — present, it's updated in place on apply;
 *  absent, it's created. */
export interface DraftLayout {
  id: string;
  originalId: number | null;
  activationEvent: string;
  activationButton: string | null;
  activationOfflineDelay: number | null;
  components: DraftComponent[];
}

/** Set when the draft was hydrated from an already-assigned layout group —
 *  apply then edits that group/its layouts in place instead of creating a
 *  new one, and only sends templates again rather than re-running the
 *  assign/unassign dance. */
export interface EditingGroup {
  id: number;
  groupIdString: string;
  /** Every layout id that existed on this group at hydration time — diffed
   *  against the draft's surviving `originalId`s on apply so layouts the
   *  user removed (event toggled off, button freed, rotation decremented)
   *  are actually deleted instead of left orphaned in the group. */
  originalLayoutIds: number[];
  /** Any other layout groups the device happens to have assigned besides
   *  the one being edited — unassigned on apply so the device ends up with
   *  exactly the one group the UI shows. Normally empty. */
  strayGroupIds: number[];
}

export const ALL_DAYS = [...DAYS_OF_WEEK];
export const SVG_CONTENT_TYPE = 'image/svg+xml';
export const DEFAULT_PLAYLIST_ITEM_DURATION = 60;
export const RENDERING_INTERVAL = 30;
export const DEFAULT_OFFLINE_DELAY = 300;
export const DEFAULT_SLIDESHOW_PERIOD = 60;

/** Short, human-scannable-enough random suffix so every assignment gets its
 *  own layout group/layouts instead of reusing a convention-based one. Not
 *  cryptographically random — just unlikely enough to collide here. Also
 *  used for purely local draft ids (layouts/components), which never reach
 *  the backend. */
export function randomId(): string {
  return Math.random().toString(36).slice(2, 8);
}

/** The runtime shape of a `Layout.components` entry (typed `unknown[]` on
 *  the model), as built by `buildComponentPayload` below. */
interface LayoutComponentRuntime {
  left: number;
  top: number;
  right: number;
  bottom: number;
  mediaType?: 'IMAGE' | 'PLAYLIST' | 'RT_ARR';
  contentRefs?: { fileName?: string }[];
  contentRaw?: { multimediaRef?: { fileName?: string } }[];
}

export function draftComponentFileNames(component: DraftComponent): string[] {
  if (component.files.length) return component.files.map((f) => f.file.name);

  const payload = component.existingPayload as LayoutComponentRuntime | undefined;
  if (!payload) return [];

  const fromRefs = payload.contentRefs?.map((r) => r.fileName).filter((n): n is string => !!n) ?? [];
  const fromPlaylist =
    payload.contentRaw?.map((r) => r.multimediaRef?.fileName).filter((n): n is string => !!n) ?? [];
  return [...fromRefs, ...fromPlaylist];
}

/** Converts a saved `Layout[]` (as returned by `LayoutService.getLayoutsForGroup`)
 *  back into editable drafts. Components keep their original saved payload
 *  verbatim (`existingPayload`) rather than trying to reconstruct picked
 *  files — a saved layout only carries `fileName`/`contentType`/`url`, not
 *  the originating file-manager id, so there's nothing to re-select in the
 *  media browser or re-resolve a fresh SAS URL from until the user actually
 *  replaces that component. */
export function hydrateLayouts(layouts: Layout[]): DraftLayout[] {
  return layouts
    .slice()
    .sort((a, b) => (a.groupOrderNum ?? 0) - (b.groupOrderNum ?? 0))
    .map((layout) => ({
      id: randomId(),
      originalId: layout.id,
      activationEvent: layout.activationEvent ?? 'DEFAULT',
      // Model types this as a single string; the dialog/page only ever read
      // the one button a BUTTON_EVENT layout is for.
      activationButton: layout.activationButtons,
      activationOfflineDelay: layout.activationOfflineDelay,
      components: ((layout.components ?? []) as LayoutComponentRuntime[]).map((c) => ({
        id: randomId(),
        left: c.left,
        top: c.top,
        right: c.right,
        bottom: c.bottom,
        contentType: c.mediaType === 'RT_ARR' ? 'SVG' : 'IMAGE',
        files: [],
        existingPayload: c,
      })),
    }));
}

function buildComponentPayload(component: DraftComponent, urls: string[]) {
  if (component.existingPayload) return component.existingPayload;

  const base = {
    left: component.left,
    top: component.top,
    right: component.right,
    bottom: component.bottom,
  };
  const files = component.files;

  if (component.contentType === 'SVG') {
    const file = files[0]?.file ?? null;
    const url = urls[0] ?? null;
    return {
      ...base,
      mediaType: 'RT_ARR',
      renderingInterval: RENDERING_INTERVAL,
      contentRefs: file && url ? [{ contentType: file.contentType, url, md5: null, contentExpiry: null }] : [],
    };
  }

  if (files.length <= 1) {
    const file = files[0]?.file ?? null;
    const url = urls[0] ?? null;
    return {
      ...base,
      mediaType: 'IMAGE',
      contentRefs: file && url
        ? [{ contentType: file.contentType, url, fileName: file.name, md5: null, contentExpiry: null }]
        : [],
    };
  }

  return {
    ...base,
    mediaType: 'PLAYLIST',
    contentRaw: files.map((pf, i) => ({
      multimediaRef: {
        contentType: pf.file.contentType,
        url: urls[i],
        fileName: pf.file.name,
        md5: null,
        contentExpiry: null,
      },
      duration: pf.duration,
      order: i + 1,
    })),
  };
}

function buildLayoutComponents$(layout: DraftLayout, fileManagerService: FileManagerService): Observable<unknown[]> {
  if (layout.components.length === 0) return of([]);
  return forkJoin(
    layout.components.map((component) =>
      component.existingPayload
        ? of(buildComponentPayload(component, []))
        : forkJoin(component.files.map((f) => fileManagerService.getFileSasUrl(f.file.id))).pipe(
            switchMap((urls) => of(buildComponentPayload(component, urls))),
          ),
    ),
  );
}

export interface ApplyServices {
  layoutService: LayoutService;
  provisioningService: ProvisioningService;
  fileManagerService: FileManagerService;
}

export interface ApplyParams {
  clientId: string;
  profileId: number;
  layouts: DraftLayout[];
  isTemplateSlideshow: boolean;
  templateSlideshowChangePeriod: number | null;
  /** Present when editing an already-assigned group in place; absent to
   *  create a brand-new group and assign it (unassigning any existing one). */
  editing: EditingGroup | null;
}

/** Runs the full "assign content" apply chain — creating or updating the
 *  layout group and its layouts, deleting any layouts dropped from the
 *  draft, assigning the operation profile, and (create mode only)
 *  swapping the device's assigned group — then pushes the result to the
 *  device. */
export function applyContentAssignment$(params: ApplyParams, services: ApplyServices): Observable<void> {
  const { clientId, profileId, layouts, isTemplateSlideshow, templateSlideshowChangePeriod, editing } = params;
  const { layoutService, provisioningService, fileManagerService } = services;

  const groupRequest = {
    groupId: editing?.groupIdString ?? `content-${randomId()}`,
    startDate: '1970-01-01',
    endDate: '9999-12-31',
    startTime: '00:00',
    endTime: '23:59',
    days: ALL_DAYS,
    isTemplateSlideshow,
    templateSlideshowChangePeriod: isTemplateSlideshow ? templateSlideshowChangePeriod : null,
  };

  const saveGroup$ = editing
    ? layoutService.updateLayoutGroup(editing.id, groupRequest)
    : layoutService.createLayoutGroup(groupRequest);

  const resolveGroupId$: Observable<number> = editing
    ? saveGroup$.pipe(switchMap(() => of(editing.id)))
    : saveGroup$.pipe(
        switchMap(() => layoutService.getLayoutGroups(0, 100)),
        switchMap((res) => {
          const group = res.data.find((g) => g.groupId === groupRequest.groupId);
          if (!group) throw new Error(`Failed to create layout group ${groupRequest.groupId}`);
          return of(group.id);
        }),
      );

  return resolveGroupId$.pipe(
    switchMap((groupId) => {
      const saveLayouts$ = forkJoin(
        layouts.length
          ? layouts.map((layout, index) =>
              buildLayoutComponents$(layout, fileManagerService).pipe(
                switchMap((components) => {
                  const activationButtons =
                    layout.activationEvent === 'BUTTON_EVENT' && layout.activationButton
                      ? [layout.activationButton]
                      : null;
                  const activationOfflineDelay =
                    layout.activationEvent === 'OFFLINE_MODE' ? layout.activationOfflineDelay : null;

                  const body: LayoutRequest = {
                    templateId: `layout-${randomId()}`,
                    groupId: groupRequest.groupId,
                    groupOrderNum: index + 1,
                    activationEvent: layout.activationEvent,
                    activationButtons,
                    activationOfflineDelay,
                    slideshowDuration: null,
                    components,
                  };
                  return layout.originalId
                    ? layoutService.updateLayout(layout.originalId, body)
                    : layoutService.createLayout(groupId, body);
                }),
              ),
            )
          : [of(null)],
      );

      // Any layout that existed when this draft was hydrated but isn't in it
      // anymore was removed (event toggled off, button freed, rotation
      // decremented) — delete it so it doesn't linger orphaned in the group.
      const survivingIds = new Set(layouts.map((l) => l.originalId).filter((id): id is number => id !== null));
      const deletedLayoutIds = editing ? editing.originalLayoutIds.filter((id) => !survivingIds.has(id)) : [];
      const deleteLayouts$ = deletedLayoutIds.length
        ? forkJoin(deletedLayoutIds.map((id) => layoutService.deleteLayout(id))).pipe(switchMap(() => of(null)))
        : of(null);

      return saveLayouts$.pipe(
        switchMap(() => deleteLayouts$),
        switchMap(() => provisioningService.assignOperationProfileToDevice(clientId, profileId)),
        switchMap(() => {
          if (!editing) {
            return layoutService
              .unassignAllLayoutGroupsFromDevice(clientId)
              .pipe(switchMap(() => layoutService.assignLayoutGroupToDevice(clientId, groupId)));
          }
          if (editing.strayGroupIds.length === 0) return of(null);
          return forkJoin(
            editing.strayGroupIds.map((id) => layoutService.removeLayoutGroupFromDevice(clientId, id)),
          );
        }),
        switchMap(() => layoutService.sendAllTemplatesToDevice(clientId)),
        switchMap(() => layoutService.sendAllTemplateGroupsToDevice(clientId)),
        switchMap(() => of(void 0)),
      );
    }),
  );
}
